import { memo, useCallback, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';

import { useRewardSystemUser } from 'hooks/rewardSystem/useRewardSystemUser';
import { useFilters } from 'hooks/useFilters';

import {
  useGetCompanyQuery,
  useGetSeasonQuery,
} from 'store/api/strapiService/rewardSystemController';

import {
  getBusinessProgressValue,
  getMarketingProgressValue,
  isMediaTaskCompleted,
} from 'utils/rewardSystem';

import RoleContentController from 'components/RoleContentController/RoleContentController';
import Button from 'components/UI/Button/Button';
import Loader from 'components/UI/Loader/Loader';
import NoData from 'components/UI/NoData/NoData';
import ActionPoints from 'components/pages/RewardSystem/Progress/ActionPoints/ActionPoints';
import CircularProgress from 'components/pages/RewardSystem/Progress/CircularProgress/CircularProgress';
import Filters from 'components/pages/RewardSystem/Progress/Filters/Filters';
import MediaBanners from 'components/pages/RewardSystem/Progress/MediaBanners/MediaBanners';
import ProgressWrapper from 'components/pages/RewardSystem/Progress/ProgressWrapper/ProgressWrapper';
import Step from 'components/pages/RewardSystem/Progress/Step/Step';

import {
  ANY_MONTH_OPTION,
  BONUS_TASK_OPTION,
  MEDIA_COMPLETED_TASKS_REQUIRED,
  PERIOD_OPTIONS,
} from 'components/config/rewardSystem';
import { ROLES } from 'config/organization';

import styles from './Progress.module.scss';

const Progress = () => {
  const { filters, setFilters, handleChange } = useFilters({
    period: '',
  });

  const { userId, type, companyId, mediaAffiliate } = useRewardSystemUser();

  const { data: companyData, isLoading: companyLoading } = useGetCompanyQuery(
    { id: companyId, type },
    { skip: !companyId },
  );

  const company = companyData?.data;

  const currentSeasonId = useMemo(
    () => company?.seasons?.[0]?.documentId,
    [company],
  );

  const { data: seasonData, isLoading: seasonLoading } = useGetSeasonQuery(
    { id: currentSeasonId, type },
    { skip: !currentSeasonId },
  );

  const season = seasonData?.data;

  const {
    step_1,
    step_2,
    period,
    marketingKPIResults,
    businessKPIResults,
    businessKPIPlan,
    bonusTaskResults,
    mediaTaskResults,
    mediaWinner,
  } = season ?? {};

  const marketingProgressValue = useMemo(
    () => getMarketingProgressValue(marketingKPIResults),
    [marketingKPIResults],
  );

  const businessProgressValue = useMemo(
    () => getBusinessProgressValue(businessKPIResults, businessKPIPlan),
    [businessKPIResults, businessKPIPlan],
  );

  const periodOptionsByRole = useMemo(
    () =>
      mediaAffiliate
        ? PERIOD_OPTIONS.filter(({ value }) => value !== ANY_MONTH_OPTION.value)
        : PERIOD_OPTIONS,
    [mediaAffiliate],
  );

  const periodFilters = useMemo(
    () =>
      !mediaAffiliate && bonusTaskResults?.length
        ? [...periodOptionsByRole, BONUS_TASK_OPTION]
        : periodOptionsByRole,
    [mediaAffiliate, bonusTaskResults?.length, periodOptionsByRole],
  );

  const getActionPoints = useCallback(
    (results, checkCompletedFn) =>
      results?.flatMap(({ id, month, description, ...item }) =>
        month === filters.period
          ? [{ id, label: description, completed: checkCompletedFn(item) }]
          : [],
      ) ?? [],
    [filters.period],
  );

  const getPeriodsCompleted = useCallback(
    (results, checkFn) =>
      periodOptionsByRole.flatMap(({ value }) => {
        const filtered = results?.filter(({ month }) => month === value);
        return filtered?.length && filtered.every(checkFn) ? [value] : [];
      }),
    [periodOptionsByRole],
  );

  const periodsCompleted = useMemo(
    () =>
      mediaAffiliate
        ? getPeriodsCompleted(mediaTaskResults, ({ users }) =>
            isMediaTaskCompleted(users, userId),
          )
        : getPeriodsCompleted(
            marketingKPIResults,
            ({ marketingKPIStatus }) => marketingKPIStatus,
          ),
    [
      mediaAffiliate,
      getPeriodsCompleted,
      mediaTaskResults,
      userId,
      marketingKPIResults,
    ],
  );

  const actionPoints = useMemo(() => {
    if (mediaAffiliate) {
      return getActionPoints(mediaTaskResults, ({ users }) =>
        isMediaTaskCompleted(users, userId),
      );
    }

    if (filters.period !== BONUS_TASK_OPTION.value) {
      return getActionPoints(
        marketingKPIResults,
        ({ marketingKPIStatus }) => marketingKPIStatus,
      );
    }

    return (
      bonusTaskResults?.map(({ id, description, bonusTaskStatus, value }) => ({
        id,
        label: description,
        completed: bonusTaskStatus,
        bonusTask: true,
        bonusValue: value,
      })) ?? []
    );
  }, [
    mediaAffiliate,
    getActionPoints,
    mediaTaskResults,
    userId,
    filters.period,
    marketingKPIResults,
    bonusTaskResults,
  ]);

  const mediaTasksCompleted = useMemo(
    () =>
      mediaAffiliate
        ? mediaTaskResults?.filter(({ users }) =>
            isMediaTaskCompleted(users, userId),
          ).length
        : 0,
    [mediaTaskResults, userId],
  );

  useEffect(() => {
    period &&
      setFilters((prev) => ({
        ...prev,
        period,
      }));
  }, [period]);

  if ([companyLoading, seasonLoading].some(Boolean)) return <Loader />;

  if (!season)
    return <NoData description="Try to reload the page or contact support" />;

  return (
    <div className={styles.root}>
      <div className={styles.steps}>
        <Step
          title={step_1?.title}
          description={step_1?.description}
          tooltipText={step_1?.tooltipText}
        >
          <ProgressWrapper
            barProps={{ value: marketingProgressValue }}
            stepsProps={{
              stepsAmount: MEDIA_COMPLETED_TASKS_REQUIRED,
              completedAmount: mediaTasksCompleted,
            }}
          />
          <Filters
            name="period"
            value={filters.period}
            items={periodFilters}
            onChange={handleChange}
            completed={periodsCompleted}
          />
          <ActionPoints
            items={actionPoints}
            columnsAmount={mediaAffiliate ? 2 : 1}
          />
        </Step>
        <RoleContentController
          roles={[ROLES.defaultUser, ROLES.mediaAffiliate]}
        >
          {[
            <Step
              key="step-2"
              title={step_2?.title}
              description={step_2?.description}
              tooltipText={step_2?.tooltipText}
            >
              <CircularProgress
                value={Math.min(businessProgressValue, 100)}
                companyLogo={company?.logo}
              />
            </Step>,
            <MediaBanners
              key="media-banners"
              tasksCompleted={mediaTasksCompleted}
              raffleResults={{
                ended: mediaWinner?.isFinished,
                won: isMediaTaskCompleted(mediaWinner?.winners, userId),
              }}
            />,
          ]}
        </RoleContentController>
      </div>
      <Button
        component={Link}
        to={company?.submitActivitiesUrl}
        rel="noopener noreferrer"
        target="_blank"
        className={styles.submitBtn}
      >
        Submit Your Marketing Activities
      </Button>
    </div>
  );
};

export default memo(Progress);
